/**
 * Stripe Payment Component
 * Enterprise-grade Stripe Payment Element integration
 *
 * @package Enterprise\BookingSystem\Components
 */

/**
 * WordPress dependencies
 */
import { useState, useImperativeHandle, forwardRef } from "@wordpress/element";
/**
 * External dependencies
 */
import {
	PaymentElement,
	useStripe,
	useElements,
} from "@stripe/react-stripe-js";
import type { StripePaymentElementOptions } from "@stripe/stripe-js";

export type StripePaymentHandle = {
	confirmPayment: () => Promise<void>;
};

type StripePaymentProps = {
	readonly amount: number;
	readonly currency: string;
	readonly isProcessing: boolean;
	readonly onError: (error: string) => void;
	readonly onSuccess: (paymentIntentId: string) => void;
};

/**
 * Stripe Payment Form Component
 * Uses forwardRef to expose confirmPayment method to parent component
 */
export const StripePaymentForm = forwardRef<StripePaymentHandle, StripePaymentProps>(
	({ amount, currency, onSuccess, onError }, ref) => {
		const stripe = useStripe();
		const elements = useElements();
		const [isReady, setIsReady] = useState(false);

		const paymentElementOptions: StripePaymentElementOptions = {
			layout: "tabs",
			paymentMethodOrder: ["card", "paypal", "cashapp", "affirm"],
		};

		/**
		 * Expose confirmPayment method to parent via ref
		 */
		useImperativeHandle(ref, () => ({
			confirmPayment: async () => {
				if (!stripe || !elements) {
					onError("Stripe is not loaded");
					throw new Error("Stripe is not loaded");
				}

				try {
					// Submit and validate the payment form
					const { error: submitError } = await elements.submit();
					if (submitError) {
						const errorMsg = submitError.message || "Payment validation failed";
						onError(errorMsg);
						throw new Error(errorMsg);
					}

					// Confirm the payment (this will authorize the card)
					const { error, paymentIntent } = await stripe.confirmPayment({
						elements,
						redirect: "if_required",
						confirmParams: {
							return_url: globalThis.location.origin + "/checkout/success",
						},
					});

					if (error) {
						const errorMsg = error.message || "An error occurred while processing payment";
						onError(errorMsg);
						throw new Error(errorMsg);
					}

					// Check payment status - for manual capture, status will be "requires_capture"
					if (paymentIntent?.status === "succeeded" || paymentIntent?.status === "requires_capture") {
						onSuccess(paymentIntent.id);
					} else {
						const errorMsg = `Payment was not successful. Status: ${paymentIntent?.status || "unknown"}`;
						onError(errorMsg);
						throw new Error(errorMsg);
					}
				} catch (error) {
					const errorMsg = error instanceof Error ? error.message : "An unexpected error occurred";
					onError(errorMsg);
					throw error;
				}
			},
		}));

		return (
			<div className="ebs-stripe-payment-form">
				<PaymentElement
					options={paymentElementOptions}
					onReady={() => { setIsReady(true); }}
				/>

				<div className="ebs-stripe-payment-info">
					<p className="ebs-stripe-payment-amount">
						<strong>Total:</strong>{" "}
						{new Intl.NumberFormat("en-US", {
							style: "currency",
							currency: currency,
						}).format(amount / 100)}
					</p>
					<p className="ebs-stripe-payment-secure">
						🔒 Secure payment powered by Stripe
					</p>
				</div>

				{!isReady && (
					<div className="ebs-stripe-loading">
						<span className="spinner" /> Loading payment methods...
					</div>
				)}
			</div>
		);
	}
);

StripePaymentForm.displayName = 'StripePaymentForm';

